import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { ContactForm } from "../components/ContactForm";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { useUI } from "../context/UIContext";
import { getTarif, TARIFE_LIST } from "../data/tarife";

export default function TopicPage() {
  const { slug } = useParams();
  const t = getTarif(slug);
  const { openUpload } = useUI();
  if (!t) return <Navigate to="/" replace />;

  const path = `/tarife/${t.slug}`;
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: t.eyebrow,
      provider: { "@type": "ProfessionalService", name: "Energie-Tarifberater", areaServed: "DE" },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: t.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <>
      <Seo title={`${t.eyebrow} – kostenlos & unabhängig`} description={t.subtitle} path={path} image={t.image} schema={schema} />
      <PageHero
        eyebrow={t.eyebrow}
        titleLines={t.titleLines}
        subtitle={t.subtitle}
        image={t.image}
        breadcrumb={[{ label: "Tarife & Anbieter", to: "/tarife/stromanbieter-vergleichen" }, { label: t.eyebrow }]}
      />

      <section className="bg-background py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12">
          {/* Content */}
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-2xl sm:text-3xl font-heading font-semibold text-navy-900 leading-snug mb-12">{t.intro}</p>
            </Reveal>

            <div className="space-y-10 mb-14">
              {t.sections.map((s, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <div data-testid={`topic-section-${i}`}>
                    <h2 className="text-xl font-heading text-navy-900 mb-3">{s.heading}</h2>
                    <p className="text-slate-600 leading-relaxed">{s.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-6 mb-12">
              {t.benefits.map((b, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="rounded-3xl border border-slate-200 bg-white p-8 h-full hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-35px_rgba(29,78,216,0.25)] transition-[transform,box-shadow] duration-500" data-testid={`topic-benefit-${i}`}>
                    <span className="inline-grid place-items-center h-11 w-11 rounded-xl bg-blue-50 text-blue-700 mb-5">
                      <Check className="h-5 w-5" strokeWidth={2.5} />
                    </span>
                    <h3 className="text-lg font-heading text-navy-900 mb-2">{b.title}</h3>
                    <p className="text-slate-500 leading-relaxed text-sm">{b.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal>
              <div className="rounded-3xl bg-blue-700 p-10 flex flex-col sm:flex-row items-center justify-between gap-6 mb-14">
                <div>
                  <p className="text-5xl font-heading font-semibold text-blue-200">{t.stat.value}</p>
                  <p className="text-slate-300 mt-2 max-w-xs">{t.stat.label}</p>
                </div>
                <button onClick={openUpload} className="group rounded-full bg-white text-blue-700 hover:bg-blue-50 px-8 py-4 font-medium transition-colors flex items-center gap-2 whitespace-nowrap" data-testid="topic-upload-btn">
                  Rechnung prüfen
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>
            </Reveal>

            {/* FAQ */}
            <Reveal>
              <h2 className="text-2xl font-heading font-semibold text-navy-900 mb-6">Häufige Fragen</h2>
              <div className="space-y-4 mb-10">
                {t.faqs.map((f, i) => (
                  <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`topic-faq-${i}`}>
                    <p className="font-heading text-navy-900 mb-2">{f.q}</p>
                    <p className="text-slate-500 text-sm leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
              <h2 className="text-lg font-heading text-navy-900 mb-4">Weitere Themen</h2>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {TARIFE_LIST.filter((x) => x.slug !== t.slug).map((x) => (
                  <Link key={x.slug} to={`/tarife/${x.slug}`} className="text-sm text-slate-500 hover:text-blue-700 transition-colors" data-testid={`topic-link-${x.slug}`}>{x.eyebrow}</Link>
                ))}
                <Link to="/gewerbestrom" className="text-sm text-slate-500 hover:text-blue-700 transition-colors" data-testid="topic-link-gewerbestrom">Gewerbestrom</Link>
              </div>
            </Reveal>
          </div>

          {/* Sticky lead form */}
          <div className="lg:col-span-5">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
              className="lg:sticky lg:top-28 rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.15)]">
              <p className="text-xs uppercase tracking-[0.2em] text-blue-700 mb-2">Kostenloser Check</p>
              <h3 className="text-2xl font-heading font-semibold text-navy-900 mb-6">Angebot in <span className="font-serif italic text-blue-700">24 Stunden</span></h3>
              <button onClick={openUpload} className="w-full group rounded-full bg-blue-700 hover:bg-blue-600 text-white py-4 font-medium transition-colors flex items-center justify-center gap-2 mb-5" data-testid="topic-form-upload-btn">
                Rechnung hochladen <ArrowUpRight className="h-4 w-4" />
              </button>
              <ContactForm compact topic={`tarif-${t.slug}`} testid={`topic-form-${t.slug}`} />
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
